import * as Radix from "@radix-ui/themes";
import * as React from "react";
import {
  MarketplaceContexts,
  Props as MarketplaceContextsProps,
} from "~/components/MarketplaceContexts";
import * as Analytics from "~/contexts/analyticsContext";
import * as ReactRouterStubs from "~/contexts/reactRouterStubsContext";
import { useInitDialog } from "~/hooks/useDialog";
import { useInitDrawer } from "~/hooks/useDrawer";
import styles from "~/root.module.css";
import * as ApiIdentitySchema from "~/routes/_marketplace.api.identity.v2/schema";

export type Props = {
  styleSheetUrl: string;
  authPromise: Promise<ApiIdentitySchema.Response>;
  segmentWriteKey?: string;
  prefabApiKey?: string;
  clerkPublishableKey?: string | null;
  algolia?: {
    appId: string;
    defaultIndex: string;
    searchKey: string;
    productTierIndex: string;
  };
  children?: React.ReactNode;
};

// Renders components outside of remix (ie WebFlow).
export const External: React.FC<Props> = (props) => {
  const reactRouterStubs = ReactRouterStubs.useVanillaJsReactRouterStubs();

  const cookiestring = RegExp("ajs_anonymous_id=[^;]+").exec(document.cookie);
  const cookieAnonymousId = decodeURIComponent(
    cookiestring ? cookiestring.toString().replace(/^[^=]+./, "") : "",
  );

  const [auth, setAuth] = React.useState<ApiIdentitySchema.Response>({
    ...ApiIdentitySchema.defaultResponse,
    anonymousId: cookieAnonymousId,
  });

  React.useEffect(() => {
    void props.authPromise.then(setAuth);
  }, [props.authPromise]);

  const authentication: MarketplaceContextsProps["authentication"] =
    React.useMemo(
      () => ({
        isVendrInternalEmployee: auth.isVendrInternalEmployee,
        isSyntheticTest: auth.isSyntheticTest,
        identity: auth.identity ?? null,
        identityV2: auth.identityV2 ?? null,
        platformOptedInCookie: auth.platformOptedInCookie ?? null,
        isAuthenticatedWithVendrPlatform: auth.isAuthenticatedWithVendrPlatform,
      }),
      [
        auth.isSyntheticTest,
        auth.isVendrInternalEmployee,
        auth.identity,
        auth.identityV2,
        auth.platformOptedInCookie,
        auth.isAuthenticatedWithVendrPlatform,
      ],
    );

  const analytics: MarketplaceContextsProps["analytics"] = React.useMemo(
    () => ({
      anonymousId: auth.anonymousId,
      analytics: props.segmentWriteKey
        ? Analytics.analytics.load({ writeKey: props.segmentWriteKey })
        : Analytics.analytics,
    }),
    [auth.anonymousId, props.segmentWriteKey],
  );

  const searchInputRef = React.useRef<HTMLDivElement | null>(null);
  const search: MarketplaceContextsProps["search"] = React.useMemo(
    () => ({
      appId: props.algolia?.appId ?? "",
      defaultIndex: props.algolia?.defaultIndex ?? "",
      productTierIndex: props.algolia?.productTierIndex ?? "",
      searchKey: props.algolia?.searchKey ?? "",
      searchInputRef: searchInputRef,
    }),
    [
      props.algolia?.appId,
      props.algolia?.defaultIndex,
      props.algolia?.searchKey,
      props.algolia?.productTierIndex,
    ],
  );

  const dialog = useInitDialog();

  const portalContainer = React.useRef<null | HTMLDivElement>(null);
  const portal: MarketplaceContextsProps["portal"] = React.useMemo(
    () => ({
      container: portalContainer,
    }),
    [],
  );

  const drawer = useInitDrawer();

  return (
    <Radix.Theme
      radius="small"
      accentColor="violet"
      grayColor="gray"
      className="frontend-ts-themes global-vars"
    >
      <MarketplaceContexts
        clerkPublishableKey={props.clerkPublishableKey}
        prefabApiKey={props.prefabApiKey}
        authentication={authentication}
        reactRouterStubs={reactRouterStubs}
        analytics={analytics}
        requestHeaders={null}
        search={search}
        dialog={dialog}
        portal={portal}
        drawer={drawer}
      >
        {props.children}
      </MarketplaceContexts>
      <div
        ref={dialog.container}
        className={styles.fullPageDialogContainer}
      ></div>
      <div ref={portalContainer}></div>
      <div ref={drawer.container}></div>
    </Radix.Theme>
  );
};
